/**
 * One shape for "a limit stopped this, come back in a moment", at every site
 * that already has a retry — slice 52, gap 5.
 *
 * ## A FIGURE WITH A LABEL, NOT A SENTENCE
 *
 * Until this slice a throttled request said "Try again in 45 seconds." as a
 * trailing sentence inside `RequestFailureAlert`, and every other retry on the
 * ads screens said nothing at all — so the same fact was a sentence in one
 * place and invisible in four. The standing rule for this programme is a
 * figure, a chip, a label; so the wait is drawn as `Wait · 45s`, in the muted
 * mono the delivery read tag already uses, beside a retry that is disabled
 * until it reaches zero.
 *
 * ## THE CONTROL KEEPS ITS NAME
 *
 * "Try again" stays "Try again" and is simply disabled while the wait runs.
 * Renaming a control under the cursor — "Try again" becoming "Wait 45s" — is
 * `CopyableValue`'s rule broken: a person who reached for a button finds a
 * different one in its place. The countdown is a separate figure beside it,
 * and when it reaches zero the button is live again without a reload.
 *
 * ## WHY THE SECONDS ARE COUNTED HERE AND NOT PRINTED ONCE
 *
 * `Retry-After` is the number of seconds from the moment the API answered.
 * Printed once and left, it is wrong a second later, and a person who waits
 * the stated time and presses is refused again. {@link useRetryWait} counts it
 * down on the browser's clock from the instant the error arrived, so what is
 * on screen is what is left, and the button re-enables itself exactly once.
 *
 * ## WHAT THIS DOES NOT DECIDE
 *
 * It says nothing about *whose* limit it was. `read_limit` (Wend's own read
 * limit) and `rate_limited` (Meta throttling the account) are different codes
 * with different sentences — the message beside this figure carries that, and
 * folding the two here would be the false sentence this codebase keeps
 * deleting. This only draws how long is left.
 *
 * ## WHY IT LIVES IN `lib/`
 *
 * It imports the API's error type and nothing else — no API client, so no
 * `import.meta.env` — which is what lets `verify-meta-ads-tab` render it and
 * run {@link retryWaitLabel} rather than grep for the string. `ErrorState`,
 * `RequestFailureAlert` and the Reports request panel are the other sites the
 * design names; each is another agent's file this turn, so they keep their own
 * drawing for now and this is the shape they adopt. Rejected: writing the
 * countdown separately at each of the five ads sites, which is how the
 * sentence came to exist in one place and nowhere else.
 */
import { useEffect, useState } from 'react';

import { isApiError } from './api/errors';

/**
 * The seconds a refusal asked for, or `null` when it named none.
 *
 * Only an `ApiError` carries `Retry-After`; anything else is a bug in this app
 * and gets no countdown rather than a guessed one. Zero and below are `null`
 * too: a wait of nothing is not a wait, and drawing `Wait · 0s` beside a live
 * button says something has to be waited for when nothing does.
 */
export function retryWaitSeconds(error: unknown): number | null {
  if (!isApiError(error)) return null;
  const seconds = error.retryAfterSeconds;
  if (seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.ceil(seconds);
}

/** The countdown as it is drawn: the label, then the figure. */
export function retryWaitLabel(seconds: number): string {
  return `Wait · ${String(Math.max(0, Math.ceil(seconds)))}s`;
}

/**
 * How many seconds are left of the wait this error asked for, ticking down;
 * `null` when there is no wait, or when it has run out.
 *
 * The effect re-runs on the error identity, so a second refusal restarts the
 * countdown even when it asked for the same number of seconds as the first.
 */
export function useRetryWait(error: unknown): number | null {
  const asked = retryWaitSeconds(error);
  const [left, setLeft] = useState<number | null>(asked);

  useEffect(() => {
    setLeft(asked);
    if (asked === null) return;
    /* The deadline, not a counter: a tab the browser throttled in the
       background resumes on the clock rather than owing the missed ticks. */
    const until = Date.now() + asked * 1000;
    const timer = window.setInterval(() => {
      const remaining = Math.ceil((until - Date.now()) / 1000);
      if (remaining <= 0) {
        /* The wait is over and there is nothing left to count. Cleared here as
           well as on the way out, because the effect only re-runs on a new
           refusal: a button somebody never pressed again would otherwise tick
           once a second for as long as the screen is open. */
        window.clearInterval(timer);
        setLeft(null);
        return;
      }
      setLeft(remaining);
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [error, asked]);

  return left;
}

/**
 * The wait beside a retry. Draws nothing when there is none, so a caller can
 * render it unconditionally and a failure that was not a limit looks exactly
 * as it did before this slice.
 */
export function RetryWait({ seconds }: { seconds: number | null }) {
  if (seconds === null) return null;
  return (
    <span
      data-role="retry-wait"
      data-seconds={seconds}
      className="font-mono text-[0.66rem] uppercase tracking-[0.14em] text-muted"
    >
      {retryWaitLabel(seconds)}
    </span>
  );
}
